import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {  RotateCcw, Radio } from "lucide-react";
import "bootstrap/dist/css/bootstrap.min.css";

export default function NotFound() {
  
  // حركة الطفو لرائد الفضاء (Zero Gravity)
  const floatAnimation = {
    animate: {
      y: [0, -20, 0],
      rotate: [0, 5, -5, 0],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // حركة موجات الرادار
  const pulseAnimation = {
    animate: {
      scale: [1, 2.5],
      opacity: [0.5, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="notfound-screen d-flex flex-column justify-content-between vh-100 overflow-hidden text-white position-relative">
      
      {/* 1. Background Stars/Noise */}
      <div className="bg-space"></div>

      {/* 2. Top Bar (Status) */}
      <div className="d-flex justify-content-between align-items-center p-4" style={{zIndex: 2}}>
        <div className="d-flex align-items-center gap-2">
            <Radio size={18} className="text-danger animate-blink" />
            <span className="small text-white-50 fw-bold letter-spacing-1">SIGNAL LOST</span>
        </div>
        <span className="small text-white-50 font-monospace">ERR_404</span>
      </div>

      {/* 3. Center Content */}
      <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center position-relative" style={{zIndex: 2}}>
        
        {/* Radar & Image Container */}
        <div className="position-relative mb-5 d-flex justify-content-center align-items-center">
            {/* موجات الرادار الخلفية */}
            <motion.div 
                className="radar-ring"
                variants={pulseAnimation}
                animate="animate"
            />
            <motion.div 
                className="radar-ring"
                variants={pulseAnimation}
                animate="animate"
                style={{ animationDelay: "1s" }} 
            />

            {/* صورة رائد الفضاء */}
            <motion.img
                src="/Errors/NotFoundImage.png"
                alt="Lost Astronaut"
                style={{ width: "220px", position: "relative", zIndex: 10 }}
                variants={floatAnimation}
                animate="animate"
            />
        </div>

        <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="display-1 fw-bold mb-0 text-gradient"
        >
            404
        </motion.h1>

        <motion.h2 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="h5 fw-normal text-white-50 mb-4 letter-spacing-2"
        >
            TRAJECTORY NOT FOUND
        </motion.h2>

        <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-white-50 text-center px-4"
            style={{ maxWidth: "350px", fontSize: "0.9rem" }}
        >
            Houston, we have a problem. The page you are looking for has drifted into deep space.
        </motion.p>
      </div>

      {/* 4. Bottom Action */}
      <motion.div 
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="p-4 w-100" 
        style={{zIndex: 2}}
      >
        <Link 
            to="/" 
            className="btn btn-mission w-100 py-3 rounded-4 d-flex align-items-center justify-content-center gap-2 shadow-lg text-decoration-none"
        >
            <RotateCcw size={20} />
            <span className="fw-bold">Return to Base</span>
        </Link>
        
        <div className="text-center mt-3">
            <small className="text-white-25 font-monospace">COORDINATES: UNKNOWN</small>
        </div>
      </motion.div>

      {/* CSS Styles */}
      <style>{`
        /* الخلفية الفضائية العميقة */
        .notfound-screen {
            background: radial-gradient(circle at bottom, #1a1a2e 0%, #000000 100%);
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        .bg-space {
            position: absolute;
            inset: 0;
            background-image: 
                radial-gradient(white, rgba(255,255,255,.2) 2px, transparent 3px),
                radial-gradient(white, rgba(255,255,255,.15) 1px, transparent 2px),
                radial-gradient(white, rgba(255,255,255,.1) 2px, transparent 3px);
            background-size: 550px 550px, 350px 350px, 250px 250px;
            background-position: 0 0, 40px 60px, 130px 270px;
            opacity: 0.4;
            z-index: 1;
        }

        .letter-spacing-1 { letter-spacing: 1px; }
        .letter-spacing-2 { letter-spacing: 3px; }

        /* تأثير النص المتدرج */
        .text-gradient {
            background: linear-gradient(to bottom, #ffffff, #6c757d);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
        }

        /* دوائر الرادار */
        .radar-ring {
            position: absolute;
            width: 200px;
            height: 200px;
            border: 1px solid rgba(13, 110, 253, 0.3); /* Primary Blue tint */
            border-radius: 50%;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            box-shadow: 0 0 15px rgba(13, 110, 253, 0.1);
        }

        /* زرار المهمة */
        .btn-mission {
            background: #0d6efd; /* Primary Blue */
            color: white;
            border: none;
            transition: all 0.3s ease;
            box-shadow: 0 0 20px rgba(13, 110, 253, 0.4);
        }
        .btn-mission:hover {
            background: #0b5ed7;
            transform: translateY(-2px);
            box-shadow: 0 0 30px rgba(13, 110, 253, 0.6);
            color: white;
        }
        .btn-mission:active {
            transform: scale(0.98);
        }

        .animate-blink {
            animation: blink 1.5s infinite;
        }

        @keyframes blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}


// import { Link } from "react-router-dom";
// // import astronaut from "../../assets/images/Errors/NotFoundImage.png";
// import "bootstrap/dist/css/bootstrap.min.css";
// import "../../assets/styles/global.css";
// export default function NotFound() {
//   return (
//     <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-dark text-white text-center px-3">
//       <h1 className="display-1 fw-bold text-primary">404</h1>

//       <h2 className="fw-semibold mt-3">Page Not Found</h2>

//       <p className="text-secondary mt-2 mb-4" style={{ maxWidth: "400px" }}>
//         Oops! It looks like the page you’re searching for has wandered off into
//         space.
//       </p>

//       <img
//         src="/Errors/NotFoundImage.png"
//         alt="Lost Astronaut"
//         style={{ width: "200px" }}
//         className="mb-4 animate__bounce"
//       />

//       <Link
//         to="/"
//         className="btn btn-primary btn-lg px-4 py-2 rounded-pill shadow"
//       >
//         Back to Home
//       </Link>

//       <small className="text-secondary mt-4">CampusPulse © 2025</small>
//     </div>
//   );
// }
